
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Bell, Check, Clock, Loader2 } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuGroup, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTaskMutations } from '@/hooks/useTaskMutations';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
  id: string;
  user_id: string;
  task_id?: string | null;
  title: string;
  message: string;
  type: 'task' | 'system';
  status: 'unread' | 'read' | 'dismissed';
  created_at: string;
  read_at?: string | null;
  action_url?: string | null;
}

export const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { completeMutation } = useTaskMutations();
  const unreadCount = notifications.filter(n => n.status === 'unread').length;

  // Fetch notifications when component mounts or user changes
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        setNotifications(data || []);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Set up real-time subscription
    const channel = supabase
      .channel('notification-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, status: 'read', read_at: new Date().toISOString() } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .eq('user_id', user?.id)
        .eq('status', 'unread');

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => n.status === 'unread' ? { ...n, status: 'read', read_at: new Date().toISOString() } : n)
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleTaskComplete = async (taskId: string, notificationId: string) => {
    try {
      await completeMutation(taskId);
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const renderNotificationItem = (notification: Notification) => {
    const time = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });
    
    return (
      <DropdownMenuItem key={notification.id} className="flex flex-col items-start p-3">
        <div className="flex flex-col w-full">
          <div className="flex justify-between items-start w-full">
            <span className={`font-medium ${notification.status === 'unread' ? 'text-primary' : 'text-muted-foreground'}`}>
              {notification.title}
            </span>
            <Badge variant="outline" className="text-xs">
              {time}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
          
          <div className="flex mt-2 gap-2">
            {notification.task_id && (
              <Button 
                variant="outline" 
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => handleTaskComplete(notification.task_id!, notification.id)}
              >
                <Check className="h-3 w-3 mr-1" /> Complete
              </Button>
            )}
            
            {notification.action_url && (
              <Button
                variant="secondary"
                size="sm"
                className="h-7 px-2 text-xs"
                asChild
                onClick={() => markAsRead(notification.id)}
              >
                <Link to={notification.action_url}>View</Link>
              </Button>
            )}
            
            {notification.status === 'unread' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs ml-auto"
                onClick={() => markAsRead(notification.id)}
              >
                Mark as read
              </Button>
            )}
          </div>
        </div>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[380px]" align="end">
        <DropdownMenuLabel className="flex justify-between items-center">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs"
              onClick={markAllAsRead}
            >
              Mark all as read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {loading ? (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Loading notifications...</span>
          </div>
        ) : notifications.length > 0 ? (
          <ScrollArea className="h-[400px]">
            <DropdownMenuGroup>
              {notifications.map(renderNotificationItem)}
            </DropdownMenuGroup>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Clock className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No notifications yet</h3>
            <p className="text-sm text-muted-foreground">
              When you have notifications, they will appear here
            </p>
          </div>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/tasks" className="w-full text-center justify-center" onClick={() => setOpen(false)}>
            View all tasks
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
